import type { User } from 'oidc-client';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/auth.context';
import { UserActionTypes, useUser } from '../context/user.context';
import { DEFAULT_IDP } from '../helpers/constants';
import LoginLoader from './login-loader.component';

/** The idp carried through the OIDC `state` by `signinRedirect`, if present. */
const getIdpFromState = (state: unknown): string | undefined => {
  if (typeof state !== 'object' || state === null) return undefined;
  const idp = (state as { idp?: unknown }).idp;
  return typeof idp === 'string' ? idp : undefined;
};

const LoginCallback = () => {
  const { t } = useTranslation();
  const { state, dispatch } = useUser();
  const { signinRedirectCallback } = useAuth();
  const navigate = useNavigate();

  /**
   * Whether a session already existed when this callback mounted.
   *
   * Every login flow reaches here unauthenticated — both `signinRedirect` call sites are
   * gated on it — so arriving with a live session means the URL was replayed rather than
   * freshly issued: browser session-restore reopening `/auth/cb?code=...`, a refresh, or
   * back-navigation. The library rejects those (the code is already consumed), and wiping
   * on that rejection drops a signed-in user onto the login form.
   *
   * A ref, not the live value: `state.auth` in the effect deps would re-run the callback.
   */
  const hadSessionOnMount = useRef(state.auth);

  useEffect(() => {
    if (signinRedirectCallback) {
      signinRedirectCallback()
        .then(async (res: User) => {
          const profile = res.profile;
          // Persist the opaque OIDC sub claim so Sentry (and other
          // non-PII consumers) can identify the user without email.
          if (profile.sub) {
            localStorage.setItem('userId', profile.sub);
          } else {
            localStorage.removeItem('userId');
          }
          // The idp is round-tripped through the OIDC `state` by `signinRedirect`. Set it here so it is
          // authoritative on every login, rather than relying on the pre-redirect app state surviving.
          const idp = getIdpFromState(res.state) ?? DEFAULT_IDP;
          dispatch({
            type: UserActionTypes.UpdateLoginStatus,
            payload: 'schema',
          });
          dispatch({
            type: UserActionTypes.UpdateUser,
            payload: {
              auth: true,
              idp,
              isDefaultIdp: idp === DEFAULT_IDP,
              user: {
                preferred_username: profile.preferred_username,
                schemas: profile['custom:schema'],
                superuser: profile['custom:superuser'],
              },
              tokens: {
                id_token: res.id_token,
                access_token: res.access_token,
                refresh_token: res.refresh_token,
                expires_at: res.expires_at,
                auth_time: profile.auth_time,
              },
            },
          });
        })
        .catch(() => {
          if (hadSessionOnMount.current) {
            // Stale replay over a good session — leave it intact. The effect below routes
            // the user on, and a genuinely dead session still logs out on the next 401.
            return;
          }

          localStorage.removeItem('UserState');
          dispatch({
            type: UserActionTypes.Reset,
          });
          // `/auth/cb` is a public route, so nothing else moves the user off it. Without this
          // the loader spins forever on a failed login. `Reset` clears idp and loginStatus, so
          // the login page renders its form rather than redirecting straight back out.
          navigate('/login', { replace: true });
        });
    }
  }, [signinRedirectCallback, dispatch, navigate]);

  useEffect(() => {
    if (state.auth) {
      // set navigation back to login page to handle schema selection.
      navigate('/login', { replace: true });
    }
  }, [state, navigate]);

  return <LoginLoader title={t('loading-user-data')} />;
};
export default LoginCallback;
